/**
 * Who owns `document.body`'s scroll lock, and how a stale one is repaired.
 *
 * A leaf module on purpose: the ownership question is asked by MUI overlays
 * (ChatGPTPresetDrawer) that have nothing to do with bottom sheets, and it is
 * answered by reading the DOM. Keeping it here rather than in
 * BottomSheetOrDialog is what stops those callers from pulling
 * react-spring-bottom-sheet — and react-spring, xstate, focus-trap,
 * body-scroll-lock and a stylesheet with it — into their bundle for two lines
 * of `document.querySelector`.
 */

let scrollLockObserver: MutationObserver | undefined;
let scrollLockRecoveryFrame: number | undefined;

/**
 * How many mounted components can currently put a bottom sheet on screen.
 *
 * The sweep below only has a job while one of them exists, so this is what
 * decides whether the observer runs at all. Counted rather than a boolean
 * because several of these are mounted at once (App renders a handful), and
 * the first one to unmount must not stand the sweep down for the rest.
 */
let mountedSheets = 0;

// One definition of "an overlay is up", per system, so the two readers below —
// the recovery sweep and the lock-ownership question callers ask — cannot drift
// apart on what counts.
//
// `data-rsbs-root` stays in the DOM after the sheet has closed; its
// `data-rsbs-state` is what moves (closed / opening / open / closing). `closing`
// deliberately counts as still up: the sheet does not release the body until its
// spring has settled, so anything opening during that window must not lock too.
const OPEN_MUI_MODAL = '.MuiModal-root:not(.MuiModal-hidden):not([aria-hidden="true"])';
const OPEN_BOTTOM_SHEET = '[data-rsbs-root]:not([data-rsbs-state="closed"])';

/**
 * `data-sheet-scroll-lock` on the body: this pair took the lock that is on it.
 *
 * The sweep needs to answer "is this `overflow: hidden` mine to remove?", and
 * the DOM cannot be asked — an inline style carries no author. Without a
 * marker the only available rule is "nothing I recognise is open, so this must
 * be stale", which is a guess that happens to be right only because MUI and
 * react-spring-bottom-sheet are the sole writers of that property today. Any
 * future one — a Capacitor keyboard handler, a fullscreen viewer, a lightbox,
 * a direct `disableBodyScroll` — would be silently unlocked a frame after
 * locking, with no way to opt out of a sweep that is global.
 *
 * Stamped when a sheet opens, because that is the moment this pair becomes
 * capable of leaving a lock behind. Cleared by the sweep, not by the close: the
 * whole point is that the bad value is written AFTER the sheet has gone.
 */
const SHEET_LOCK_MARKER = 'sheetScrollLock';

/**
 * Is a bottom sheet currently the owner of the body's scroll lock?
 *
 * The question a MUI overlay has to ask before locking the body itself. MUI's
 * own ModalManager keeps a stack, so MUI nesting inside MUI is already safe; the
 * hazard is CROSS-library, because react-spring-bottom-sheet saves and restores
 * the same single `document.body.style.overflow` with no knowledge of MUI's
 * stack. Two owners, and whichever restores LAST wins — so a sheet that closes
 * before the modal above it leaves MUI to write back the `hidden` it saw on the
 * way in, and the page is frozen with no overlay left to explain it.
 *
 * A modal that asks this first, and passes `disableScrollLock` when the answer
 * is yes, is never the second owner. When the answer is no it locks normally,
 * which is what keeps the page underneath still.
 */
export const bottomSheetOwnsScrollLock = (): boolean =>
  typeof document !== 'undefined' && !!document.querySelector(OPEN_BOTTOM_SHEET);

/**
 * Claim the body's lock for the sheet/modal pair. Called on the sheet's opening
 * edge; see `SHEET_LOCK_MARKER` for why the claim outlives the sheet.
 */
export const markSheetScrollLock = (): void => {
  if (typeof document === 'undefined' || !document.body) return;
  document.body.dataset[SHEET_LOCK_MARKER] = '1';
};

/** Is a repair still owed — a claim made that the sweep has not settled yet? */
const sheetLockOutstanding = (): boolean =>
  typeof document !== 'undefined' && !!document.body && document.body.dataset[SHEET_LOCK_MARKER] === '1';

const disconnectScrollLockRecovery = (): void => {
  if (scrollLockRecoveryFrame) {
    cancelAnimationFrame(scrollLockRecoveryFrame);
    scrollLockRecoveryFrame = undefined;
  }
  if (scrollLockObserver) {
    scrollLockObserver.disconnect();
    scrollLockObserver = undefined;
  }
};

/**
 * Both MUI ModalManager and react-spring-bottom-sheet save the body's previous
 * overflow value. If their close order is inverted, MUI can restore the
 * bottom sheet's saved `hidden` value after the sheet has already unlocked.
 * Only repair that stale value when neither overlay system still has an active
 * surface; an open modal/sheet must continue to own the lock.
 *
 * And only when this pair is the one that claimed it. A lock nobody here took
 * is left exactly as it is — see `SHEET_LOCK_MARKER`.
 */
export const releaseStaleBodyScrollLock = (): void => {
  if (typeof document === 'undefined' || !document.body) return;

  // Someone is still legitimately holding the body. Nothing to settle yet, and
  // the claim stays open — the bad write may not even have happened.
  if (document.querySelector(`${OPEN_MUI_MODAL}, ${OPEN_BOTTOM_SHEET}`)) return;

  if (!sheetLockOutstanding()) return;

  if (document.body.style.overflow === 'hidden') {
    document.body.style.removeProperty('overflow');
  }
  delete document.body.dataset[SHEET_LOCK_MARKER];

  // Settled, and nothing on screen can open a sheet — so there is nothing left
  // to watch for. A sheet still mounted keeps the sweep armed for its next open.
  if (mountedSheets === 0) disconnectScrollLockRecovery();
};

const startScrollLockRecovery = (): void => {
  if (typeof document === 'undefined' || typeof MutationObserver === 'undefined' || scrollLockObserver) return;

  const scheduleRecovery = () => {
    if (scrollLockRecoveryFrame) cancelAnimationFrame(scrollLockRecoveryFrame);
    scrollLockRecoveryFrame = requestAnimationFrame(releaseStaleBodyScrollLock);
  };

  scrollLockObserver = new MutationObserver(scheduleRecovery);
  scrollLockObserver.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-hidden', 'class', 'data-rsbs-state']
  });
  scheduleRecovery();
};

/**
 * Arm the recovery sweep for as long as a bottom sheet can exist, and return
 * the function that stands it down again.
 *
 * Scoped this way because the observer is the expensive half of this file:
 * `childList + subtree` on the body means every node the app inserts or removes
 * anywhere allocates a MutationRecord and wakes the callback. It used to be
 * installed by the first BottomSheetOrDialog to mount — including the desktop
 * branch, which renders a MUI Dialog and no sheet at all — and never removed,
 * so a desktop session paid for it for its whole life to guard a conflict that
 * could not arise there.
 *
 * The window is the CALLER'S MOUNT, not its `open`: the freeze is written after
 * the sheet has closed, by the modal above it, so standing down on close would
 * stand down exactly when the repair is needed.
 */
export const acquireScrollLockRecovery = (): (() => void) => {
  mountedSheets += 1;
  startScrollLockRecovery();

  return () => {
    mountedSheets = Math.max(0, mountedSheets - 1);
    if (mountedSheets > 0) return;

    // One last look on the way out.
    releaseStaleBodyScrollLock();

    // If a claim is still open here, an overlay outlived the last sheet and the
    // bad write has not landed yet. Staying armed is the difference between
    // narrowing this observer's life and narrowing its coverage; the sweep
    // disconnects itself above once the repair lands.
    if (sheetLockOutstanding()) return;

    disconnectScrollLockRecovery();
  };
};
